import { type PopupSize, openBlankPopup } from './popupWindow';
import { waitForPopupClosed } from './waitForPopupClosed';
import { waitForPopupMessage } from './waitForPopupMessage';

export class OAuthInitError extends Error {
  constructor(cause: unknown) {
    super('Failed to initialize OAuth flow');
    this.name = 'OAuthInitError';
    this.cause = cause;
  }
}

const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000;

type OpenOAuthPopupParams<T> = {
  getUrl: () => Promise<string>;
  validate: (data: unknown) => data is T;
  allowedOrigin: string;
  size?: PopupSize;
  timeoutMs?: number;
};

export type OAuthPopupHandle<T> = {
  result: Promise<T | null>;
  close: () => void;
};

export function openOAuthPopup<T>({
  getUrl,
  validate,
  allowedOrigin,
  size,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: OpenOAuthPopupParams<T>): OAuthPopupHandle<T> {
  const popup = openBlankPopup(size);
  const controller = new AbortController();
  const { signal } = controller;

  getUrl()
    .then((url) => {
      if (!popup.closed) {
        popup.navigate(url);
      }
    })
    .catch((error) => {
      popup.close();
      controller.abort(new OAuthInitError(error));
    });

  const result = (async () => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      return await Promise.race([
        waitForPopupMessage<T>({
          allowedOrigin,
          isSource: popup.isSource,
          validate,
          signal,
        }),
        waitForPopupClosed(popup, { signal }).then(() => null),
        new Promise<null>((resolve) => {
          timeoutId = setTimeout(() => {
            popup.close();
            resolve(null);
          }, timeoutMs);
        }),
      ]);
    } finally {
      clearTimeout(timeoutId);
      controller.abort();
    }
  })();

  const close = () => {
    controller.abort();
    popup.close();
  };

  return { result, close };
}
